import { Link } from "react-router-dom";
import { Lock, ShieldCheck, KeyRound, Server, RefreshCw, EyeOff, FileLock2 } from "lucide-react";

const PILLARS = [
  {
    icon: Lock,
    title: "Encryption in transit",
    body: "All traffic between your browser and our servers is encrypted with TLS 1.2+, provisioned through Let’s Encrypt and managed by Vercel and Render.",
  },
  {
    icon: FileLock2,
    title: "Encryption at rest",
    body: "Submission and chat data are stored in MongoDB Atlas with AES-256 encryption at rest, hosted in a hardened region with audited physical access controls.",
  },
  {
    icon: KeyRound,
    title: "Access controls",
    body: "Administrative access to leads and reports is gated by a per-environment admin token. No third party has standing access to your data outside of the contracted sub-processors disclosed in our Privacy Policy.",
  },
  {
    icon: Server,
    title: "Vetted infrastructure",
    body: "We host on SOC 2 Type II–attested infrastructure (Vercel for the front end, Render for the back-end API, MongoDB Atlas for the database, Cloudflare at the edge).",
  },
  {
    icon: EyeOff,
    title: "Confidentiality",
    body: "Assessment responses and chat transcripts are visible only to authorized members of the Freedom In Me team and are used solely to inform our conversation with you.",
  },
  {
    icon: RefreshCw,
    title: "Ongoing review",
    body: "We review dependencies, access logs, and our sub-processor list on a regular cadence and apply security patches as they become available.",
  },
];

export default function Security() {
  return (
    <main data-testid="page-security" className="pt-20 bg-[var(--bone)]">
      {/* HERO */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-28 relative overflow-hidden">
        <div className="grain absolute inset-0"></div>
        <div
          className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(201,164,90,0.18), transparent 60%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
          <div className="eyebrow-light">Trust</div>
          <h1 className="font-display text-[44px] md:text-[64px] leading-[1.02] mt-5">
            Security &amp; Trust
          </h1>
          <p className="mt-8 max-w-2xl text-[18px] leading-[1.6] text-[var(--navy-100)]/85">
            Many of the organizations we serve operate in regulated, mission-critical environments — including public-sector, healthcare, and Fortune-class enterprises. Here is how we protect the information you share with us.
          </p>
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow">How We Operate</div>
          <h2 className="font-display text-[34px] md:text-[48px] leading-[1.05] text-[var(--navy-900)] mt-5 max-w-3xl">
            Six commitments that shape every part of the platform.
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <article key={p.title} className="bg-white border border-[var(--navy-900)]/8 p-7">
                  <div className="w-11 h-11 rounded-sm bg-[var(--navy-50)] flex items-center justify-center text-[var(--navy-900)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-[20px] leading-[1.2] text-[var(--navy-900)] mt-6">{p.title}</h3>
                  <p className="mt-3 text-[14px] leading-[1.65] text-[var(--muted)]">{p.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUB-PROCESSORS */}
      <section className="bg-[var(--cream)] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Sub-Processors</div>
            <h2 className="font-display text-[34px] md:text-[44px] leading-[1.05] text-[var(--navy-900)] mt-5">
              The vendors that help us operate.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-[var(--muted)] max-w-md">
              We disclose every third party that processes your information. Each is contractually obligated to protect your data and to use it only on our behalf.
            </p>
          </div>

          <div className="lg:col-span-7">
            <table className="w-full text-left text-[14px]">
              <thead>
                <tr className="border-b border-[var(--navy-900)]/15 text-[10px] tracking-[0.22em] uppercase text-[var(--muted)] font-semibold">
                  <th className="py-3 pr-4">Vendor</th>
                  <th className="py-3 pr-4">Purpose</th>
                  <th className="py-3">Region</th>
                </tr>
              </thead>
              <tbody className="text-[var(--navy-900)]">
                {[
                  ["Anthropic, PBC", "Claude AI model — generates A.L.I.G.N. reports and chat responses", "USA"],
                  ["Resend, Inc.", "Transactional email delivery", "USA"],
                  ["MongoDB, Inc. (Atlas)", "Encrypted database storage", "USA"],
                  ["Vercel, Inc.", "Front-end hosting and CDN", "Global edge"],
                  ["Render Services, Inc.", "Back-end API hosting", "USA"],
                  ["Cloudflare, Inc.", "Edge networking and DDoS protection", "Global edge"],
                ].map(([v, p, r]) => (
                  <tr key={v} className="border-b border-[var(--navy-900)]/10 last:border-0">
                    <td className="py-4 pr-4 font-semibold">{v}</td>
                    <td className="py-4 pr-4 text-[var(--muted)]">{p}</td>
                    <td className="py-4 text-[var(--muted)]">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INCIDENT / CONTACT */}
      <section className="bg-[var(--navy-950)] text-[var(--bone)] py-20 lg:py-28 relative overflow-hidden">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow-light">Reporting</div>
            <h2 className="font-display text-[32px] md:text-[42px] leading-[1.06] mt-5">
              Report a security concern.
            </h2>
            <p className="mt-6 max-w-xl text-[16px] leading-[1.65] text-[var(--navy-100)]/85">
              If you believe you have identified a vulnerability or have a security question about Freedom In Me services, please contact us directly. We acknowledge legitimate reports within two business days.
            </p>
          </div>
          <div className="lg:col-span-5">
            <a
              href="mailto:info@itsthefreedominme.com?subject=Security%20Report"
              className="btn-gold inline-flex items-center gap-2 px-7 py-4 rounded-full text-[12px] tracking-[0.24em] uppercase font-semibold"
            >
              <ShieldCheck size={16} /> info@itsthefreedominme.com
            </a>
            <p className="mt-6 text-[12px] tracking-[0.18em] uppercase text-[var(--navy-100)]/55">
              See also our <Link to="/privacy" className="text-[var(--gold-soft)] underline">Privacy Policy</Link> &amp; <Link to="/terms" className="text-[var(--gold-soft)] underline">Terms of Use</Link>.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
